/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  IExceptionList,
  IExceptionListById,
  IExceptionListByReport,
  IExceptionListByReportParams,
  IExceptionListCreate,
  IExceptionListParams,
  IUpdateExceptionList,
} from "@/@types/exception-list";
import { Paginated } from "@/@types/paginated";
import { Tags } from "@/enum/tags";
import { apiSlice } from "./http";
import {
  IHistoryListByReport,
  IHistoryListByReportParams,
} from "@/@types/engine-history";

export const ExceptionListApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    createExceptionList: builder.mutation<void, IExceptionListCreate[]>({
      query: (body) => ({
        url: "/engine-fiscal/exception",
        method: "POST",
        body,
      }),
      invalidatesTags: [Tags.EXCEPTION_LIST],
    }),
    getExceptionList: builder.query<
      Paginated<IExceptionList>,
      IExceptionListParams
    >({
      query: ({
        meta,
        search_ncm,
        search_sku,
        type,
        exception_type,
        state,
        search,
      }) => {
        const paramsObj: Record<string, string | undefined> = {
          search_ncm,
          search_sku,
          type: type?.toString(),
          exception_type: exception_type?.toString(),
          state,
          search,
          perPage: meta.perPage?.toString(),
          page: meta.page?.toString(),
        };
        const params = new URLSearchParams(
          Object.entries(paramsObj)
            .filter(([_, value]) => value !== undefined && value !== '')
            .reduce(
              (acc, [key, value]) => ({ ...acc, [key]: value as string }),
              {}
            )
        );

        return {
          url: `/engine-fiscal/exception?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: [Tags.EXCEPTION_LIST],
    }),
    createExceptionReport: builder.mutation<
      IExceptionListByReport,
      { params: IExceptionListByReportParams }
    >({
      query: ({ params }) => {
        const urlParams = new URLSearchParams(
          params as unknown as Record<string, string>
        );
        return {
          url: `/engine-fiscal/records?${urlParams.toString()}`,
          method: "POST",
        };
      },
      invalidatesTags: [Tags.EXCEPTION_LIST],
    }),

    getExceptionListById: builder.query<IExceptionListById, number>({
      query: (id) => ({
        url: `/engine-fiscal/exception/${id}`,
        method: "GET",
      }),
      providesTags: [Tags.EXCEPTION_LIST],
    }),
    updateExceptionList: builder.mutation<
      void,
      { id: number; body: IUpdateExceptionList }
    >({
      query: ({ id, body }) => ({
        url: `/engine-fiscal/exception/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: [Tags.EXCEPTION_LIST],
    }),
    createExportModelEngine: builder.mutation<IExceptionListByReport, void>({
      query: () => ({
        url: "/engine-fiscal/model-excel",
        method: "POST",
      }),
      invalidatesTags: [Tags.EXCEPTION_LIST],
    }),

    sendExceptionExcel: builder.mutation<
      { success: boolean; message?: string } | void,
      FormData
    >({
      queryFn: async (formData, _api, _extraOptions, fetchWithBQ) => {
        try {
          const result = await fetchWithBQ({
            url: "/engine-fiscal/import-exception",
            method: "POST",
            body: formData,
          });

          if (result.error) {
            return { error: result.error };
          }

          const jsonData = result.data as any;

          if (
            jsonData &&
            jsonData.success === false &&
            jsonData.excelBase64 &&
            jsonData.filename
          ) {
            const byteCharacters = atob(jsonData.excelBase64);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], {
              type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = jsonData.filename || "erros_importacao.xlsx";
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);

            return {
              error: {
                status: 200,
                data: {
                  hasErrors: true,
                  message: jsonData.message ||
                    "Foram encontrados erros na importação. O arquivo com os erros foi baixado automaticamente.",
                  totalLines: jsonData.totalLines,
                  successCount: jsonData.successCount,
                  errorCount: jsonData.errorCount,
                  errors: jsonData.errors,
                },
              },
            };
          }

          return { data: jsonData };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              data: error,
            },
          };
        }
      },
    }),
    createReportHistory: builder.mutation<
      IHistoryListByReport,
      { params: IHistoryListByReportParams }
    >({
      query: ({ params }) => {
        const urlParams = new URLSearchParams(
          params as unknown as Record<string, string>
        );
        return {
          url: `/engine-fiscal/records-history?${urlParams.toString()}`,
          method: "POST",
        };
      },
      invalidatesTags: [Tags.EXCEPTION_LIST],
    }),
  }),
});

export const {
  useCreateExceptionListMutation,
  useGetExceptionListQuery,
  useGetExceptionListByIdQuery,
  useUpdateExceptionListMutation,
  useCreateExceptionReportMutation,
  useCreateExportModelEngineMutation,
  useSendExceptionExcelMutation,
  useCreateReportHistoryMutation,
} = ExceptionListApi;

export const {
  endpoints: {
    getExceptionList: { select: selectExceptionList },
  },
} = ExceptionListApi;
